<template>
  <div class="clue-management">
    <!-- 主要内容区 -->
    <main class="max-w-7xl mx-auto px-4">
      <!-- 页面标题 -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-800 mb-2">{{ $store.getters.t('clue_management') }}</h1>
        <p class="text-gray-600">{{ $store.getters.t('clue_management_desc') }}</p>
      </div>

      <!-- 搜索和筛选区域 -->
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <!-- 状态筛选 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $store.getters.t('status_filter') }}</label>
            <select 
              v-model="filters.status" 
              @change="loadData"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
            >
              <option value="">{{ $store.getters.t('all_status') }}</option>
              <option value="待处理">{{ $store.getters.t('pending_leads') }}</option>
              <option value="预跟踪">{{ $store.getters.t('contracting_leads') }}</option>
              <option value="已签约">{{ $store.getters.t('signed_leads') }}</option>
              <option value="已淘汰">{{ $store.getters.t('eliminated_leads') }}</option>
            </select>
          </div>

          <!-- 开始日期 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $store.getters.t('start_date') }}</label>
            <input 
              type="date" 
              v-model="filters.startDate"
              @change="loadData"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
            >
          </div>

          <!-- 结束日期 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $store.getters.t('end_date') }}</label>
            <input 
              type="date" 
              v-model="filters.endDate"
              @change="loadData"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
            >
          </div>

          <!-- 搜索按钮 -->
          <div class="flex items-end">
            <button 
              @click="loadData"
              class="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <i class="fas fa-search mr-2"></i>
              {{ $store.getters.t('search') }}
            </button>
          </div>
        </div>
      </div>

      <!-- 数据统计 -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <i class="fas fa-users text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('total_leads') }}</p>
              <p class="text-2xl font-bold">{{ stats.total || 0 }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-4">
              <i class="fas fa-clock text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('pending_leads') }}</p>
              <p class="text-2xl font-bold">{{ stats.pending || 0 }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <i class="fas fa-handshake text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('contracting_leads') }}</p>
              <p class="text-2xl font-bold">{{ stats.contracting || 0 }}</p>
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-red-100 text-red-600 mr-4">
              <i class="fas fa-times-circle text-xl"></i>
            </div>
            <div>
              <p class="text-gray-500 text-sm">{{ $store.getters.t('eliminated_leads') }}</p>
              <p class="text-2xl font-bold">{{ stats.eliminated || 0 }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 线索列表 -->
      <div class="bg-white rounded-lg shadow-md">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-xl font-bold">{{ $store.getters.t('lead_list') }}</h2>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="p-8 text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p class="mt-2 text-gray-600">{{ $store.getters.t('loading_data') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="error" class="p-8 text-center">
          <div class="text-red-600 mb-4">
            <i class="fas fa-exclamation-triangle text-2xl"></i>
          </div>
          <p class="text-gray-600">{{ error }}</p>
          <button @click="loadData" class="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            {{ $store.getters.t('retry') }}
          </button>
        </div>

        <!-- 数据表格 -->
        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">ID</th>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ $store.getters.t('social_account') }}</th>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ $store.getters.t('platform') }}</th>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ $store.getters.t('country') }}</th>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ $store.getters.t('status') }}</th>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ $store.getters.t('follow_priority') }}</th>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ $store.getters.t('create_time') }}</th>
                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{{ $store.getters.t('actions') }}</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="clue in clues" :key="clue.id" class="hover:bg-gray-50">
                <td class="px-6 py-3 whitespace-nowrap font-medium text-gray-900">{{ clue.id }}</td>
                <td class="px-6 py-3 whitespace-nowrap font-medium text-gray-900">{{ clue.socialHandle }}</td>
                <td class="px-6 py-3 whitespace-nowrap font-medium text-gray-900">{{ clue.platform }}</td>
                <td class="px-6 py-3 whitespace-nowrap font-medium text-gray-900">{{ clue.country }}</td>
                <td class="px-6 py-3 whitespace-nowrap">
                  <span :class="getStatusClass(clue.status)" class="px-2 py-1 font-medium rounded-full">
                    {{ clue.status }}
                  </span>
                </td>
                <td class="px-6 py-3 whitespace-nowrap">
                  <span :class="getPriorityClass(clue.followPriority)" class="px-2 py-1 font-medium rounded-full">
                    {{ getPriorityText(clue.followPriority) }}
                  </span>
                </td>
                <td class="px-6 py-3 whitespace-nowrap font-medium text-gray-900">{{ formatDate(clue.createdAt) }}</td>
                <td class="px-6 py-3 whitespace-nowrap font-medium">
                  <div class="flex space-x-2">
                    <button 
                      @click="showStatusModal(clue)"
                      class="text-purple-600 hover:text-purple-900"
                    >
                      <i class="fas fa-edit mr-1"></i>
                      {{ $store.getters.t('change_status') }}
                    </button>
                    <button 
                      @click="showPriorityModal(clue)"
                      class="text-yellow-600 hover:text-yellow-700"
                    >
                      <i class="fas fa-sort-amount-up mr-1"></i>
                      {{ $store.getters.t('change_priority') }}
                    </button>
                    <button 
                      @click="viewDetail(clue.id)"
                      class="text-blue-600 hover:text-blue-900"
                    >
                      <i class="fas fa-eye mr-1"></i>
                      {{ $store.getters.t('view_detail') }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页 -->
        <div v-if="!loading && !error" class="px-6 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              {{ $store.getters.t('showing_records') }} {{ (pagination.pageIndex - 1) * pagination.pageSize + 1 }} {{ $store.getters.t('to') }} 
              {{ Math.min(pagination.pageIndex * pagination.pageSize, pagination.totalCount) }} {{ $store.getters.t('of') }} {{ pagination.totalCount }} {{ $store.getters.t('records') }}
            </div>
            <div class="flex space-x-2">
              <button 
                @click="changePage(pagination.pageIndex - 1)"
                :disabled="pagination.pageIndex <= 1"
                class="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {{ $store.getters.t('previous_page') }}
              </button>
              <span class="px-3 py-1 text-sm text-gray-700">
                {{ pagination.pageIndex }} / {{ pagination.totalPages }}
              </span>
              <button 
                @click="changePage(pagination.pageIndex + 1)"
                :disabled="pagination.pageIndex >= pagination.totalPages"
                class="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {{ $store.getters.t('next_page') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 状态变更模态框 -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">{{ $store.getters.t('change_lead_status') }}</h3>
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">{{ $store.getters.t('lead_id') }}: {{ selectedClue?.id }}</p>
            <p class="text-sm text-gray-600 mb-4">{{ $store.getters.t('social_account') }}: {{ selectedClue?.socialHandle }}</p>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $store.getters.t('new_status') }}</label>
            <select 
              v-model="newStatus"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
            >
              <option value="待处理">{{ $store.getters.t('pending_leads') }}</option>
              <option value="预跟踪">{{ $store.getters.t('contracting_leads') }}</option>
              <option value="已签约">{{ $store.getters.t('signed_leads') }}</option>
              <option value="已淘汰">{{ $store.getters.t('eliminated_leads') }}</option>
            </select>
          </div>
          <div class="flex justify-end space-x-3">
            <button 
              @click="closeModal"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {{ $store.getters.t('cancel') }}
            </button>
            <button 
              @click="updateStatus"
              :disabled="updating"
              class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {{ updating ? $store.getters.t('updating') : $store.getters.t('confirm_update') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 优先级变更模态框 -->
    <div v-if="showPriorityModalFlag" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">{{ $store.getters.t('change_lead_priority') }}</h3>
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">{{ $store.getters.t('lead_id') }}: {{ selectedClueForPriority?.id }}</p>
            <p class="text-sm text-gray-600 mb-4">{{ $store.getters.t('social_account') }}: {{ selectedClueForPriority?.socialHandle }}</p>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $store.getters.t('new_priority') }}</label>
            <select 
              v-model="newPriority"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500"
            >
              <option :value="1">1</option>
              <option :value="2">2</option>
              <option :value="3">3</option>
              <option :value="4">4</option>
              <option :value="5">5</option>
              <option :value="6">6</option>
              <option :value="7">7</option>
              <option :value="8">8</option>
              <option :value="9">9</option>
              <option :value="10">10</option>
            </select>
          </div>
          <div class="flex justify-end space-x-3">
            <button 
              @click="closePriorityModal"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {{ $store.getters.t('cancel') }}
            </button>
            <button 
              @click="updatePriority"
              :disabled="updatingPriority"
              class="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
            >
              {{ updatingPriority ? $store.getters.t('updating') : $store.getters.t('confirm_update') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import creatorService from '@/services/creatorService'

export default {
  name: 'ClueManagementPage',
  data() {
    return {
      clues: [],
      loading: false,
      error: null,
      filters: {
        status: '',
        startDate: '',
        endDate: ''
      },
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0
      },
      stats: {
        total: 0,
        pending: 0,
        contracting: 0,
        eliminated: 0
      },
      showModal: false,
      selectedClue: null,
      newStatus: '',
      updating: false,
      // 优先级相关
      showPriorityModalFlag: false,
      selectedClueForPriority: null,
      newPriority: 5,
      updatingPriority: false
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    /**
     * 加载数据
     */
    async loadData() {
      this.loading = true
      this.error = null
      
      try {
        const params = {
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
          status: this.filters.status || null,
          startDate: this.filters.startDate || null,
          endDate: this.filters.endDate || null
        }

        const data = await creatorService.getCreatorLeads(params)
        
        this.clues = data.data || []
        this.pagination.totalCount = data.totalCount || 0
        this.pagination.totalPages = data.totalPages || 0
        
        // 计算统计数据
        this.calculateStats()
        
      } catch (error) {
        console.error('加载线索数据失败:', error)
        this.error = '加载数据失败，请重试'
      } finally {
        this.loading = false
      }
    },

    /**
     * 计算统计数据
     */
    calculateStats() {
      this.stats = {
        total: this.pagination.totalCount,
        pending: 0,
        contracting: 0,
        eliminated: 0
      }

      this.clues.forEach(clue => {
        switch (clue.status) {
          case '待处理':
            this.stats.pending++
            break
          case '预跟踪':
            this.stats.contracting++
            break
          case '已淘汰':
            this.stats.eliminated++
            break
        }
      })
    },

    /**
     * 切换页码
     */
    async changePage(pageIndex) {
      if (pageIndex < 1 || pageIndex > this.pagination.totalPages) return
      this.pagination.pageIndex = pageIndex
      await this.loadData()
    },

    /**
     * 显示状态变更模态框
     */
    showStatusModal(clue) {
      this.selectedClue = clue
      this.newStatus = clue.status
      this.showModal = true
    },

    /**
     * 关闭模态框
     */
    closeModal() {
      this.showModal = false
      this.selectedClue = null
      this.newStatus = ''
    },

    /**
     * 更新状态
     */
    async updateStatus() {
      if (!this.selectedClue || !this.newStatus) return

      this.updating = true
      try {
        await creatorService.updateCreatorLeadStatus(
          this.selectedClue.id,
          this.newStatus
        )
        
        // 更新本地数据
        this.selectedClue.status = this.newStatus
        
        // 重新计算统计
        this.calculateStats()
        
        this.closeModal()
        
        // 显示成功提示
        this.$message.success(this.$store.getters.t('status_update_success'))
        
      } catch (error) {
        console.error('更新状态失败:', error)
        this.$message.error(this.$store.getters.t('status_update_failed'))
      } finally {
        this.updating = false
      }
    },

    /**
     * 查看详情
     */
    viewDetail(id) {
      this.$router.push(`/creator-detail/${id}`)
    },

    /**
     * 格式化日期
     */
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    /**
     * 获取状态样式类
     */
    getStatusClass(status) {
      switch (status) {
        case '待处理':
          return 'bg-yellow-100 text-yellow-800'
        case '预跟踪':
          return 'bg-green-100 text-green-800'
        case '已签约':
          return 'bg-blue-100 text-blue-800'
        case '已淘汰':
          return 'bg-red-100 text-red-800'
        default:
          return 'bg-gray-100 text-gray-800'
      }
    },

    /**
     * 显示优先级变更模态框
     */
    showPriorityModal(clue) {
      this.selectedClueForPriority = clue
      this.newPriority = clue.followPriority || 5
      this.showPriorityModalFlag = true
    },

    /**
     * 关闭优先级模态框
     */
    closePriorityModal() {
      this.showPriorityModalFlag = false
      this.selectedClueForPriority = null
      this.newPriority = 5
    },

    /**
     * 更新优先级
     */
    async updatePriority() {
      if (!this.selectedClueForPriority || this.newPriority === null) return

      this.updatingPriority = true
      try {
        await creatorService.updateCreatorLeadFollowPriority(
          this.selectedClueForPriority.id,
          this.newPriority
        )
        
        // 更新本地数据
        this.selectedClueForPriority.followPriority = this.newPriority
        
        this.closePriorityModal()
        
        // 显示成功提示
        this.$message.success(this.$store.getters.t('priority_update_success'))
        
      } catch (error) {
        console.error('更新优先级失败:', error)
        this.$message.error(this.$store.getters.t('priority_update_failed'))
      } finally {
        this.updatingPriority = false
      }
    },

    /**
     * 获取优先级文本
     */
    getPriorityText(priority) {
      if (!priority) return 'N/A'
      return priority
    },

    /**
     * 获取优先级样式类
     */
    getPriorityClass(priority) {
      if (!priority) return 'bg-gray-100 text-gray-800'
      if (priority <= 2) {
        return 'bg-red-100 text-red-800'
      } else if (priority <= 4) {
        return 'bg-yellow-100 text-yellow-800'
      } else if (priority <= 6) {
        return 'bg-green-100 text-green-800'
      } else if (priority <= 8) {
        return 'bg-blue-100 text-blue-800'
      } else {
        return 'bg-gray-100 text-gray-800'
      }
    }
  }
}
</script> 